import React from "react";
import * as Icon from "react-feather";
import clsx from "clsx";

import "./quickbooks-trial-balance-panel.css";
import WComponent from "../../../common/WComponent";
import ToolsSelect from "../kit/ToolsSelect";
import ToolsAcknowledgeNotice from "../kit/ToolsAcknowledgeNotice";
import ToolsSummaryCard from "../kit/ToolsSummaryCard";
import QuickBooksGlyph from "./QuickBooksGlyph";
import BookkeepingService from "../../../../services/BookkeepingService";
import QboService from "../../../../services/QboService";
import UtilityService from "../../../../services/UtilityService";

const NEW_CHART_ACCOUNT = "new";
const LINES_SHOWN_BEFORE_EXPANDING = 12;

class QuickBooksTrialBalancePanel extends WComponent {
	state = {
		loading: true,
		connection: null,
		preview: null,
		chartAccounts: [],
		statementLines: [],
		choices: {},
		acknowledged: {},
		earningsCloseAccountId: null,
		pulling: false,
		linking: false,
		committing: false,
		committed: null,
		clearing: false,
		showAllLines: false,
		error: "",
	};

	linkingSection = React.createRef();

	onGoToLinking = () => {
		const section = this.linkingSection.current;
		if (section) section.scrollIntoView({ behavior: "smooth", block: "start" });
	};

	componentDidMount = async () => {
		const { clientId, wpfEngagementId } = this.props;
		const [connection, latest, chart, taxonomy] = await Promise.all([
			QboService.getClientConnection({ clientId }),
			BookkeepingService.previewQuickBooksTrialBalance({ clientId, wpfEngagementId }),
			BookkeepingService.getClientCoA({ clientId }),
			BookkeepingService.getCanonicalTaxonomy(),
		]);

		const chartAccounts = chart && chart.source === "client" ? chart.accounts : [];
		const statementLines = taxonomy.map(node => ({ value: node.canonical_code, label: node.display_name }));
		const error = connection.error || latest.error || "";
		await this.update({ loading: false, connection, chartAccounts, statementLines, error });
		if (this.props.startByPulling && connection.connected) {
			await this.onPull();
			return;
		}
		if (latest.preview) await this.showPreview({ preview: latest.preview });
	};

	showPreview = async ({ preview }) => {
		const choices = BookkeepingService.proposeQuickBooksLinkChoices({ accountsNeedingLinking: preview.accountsNeedingLinking });
		const earningsClose = preview.earningsClose;
		const earningsCloseAccountId = this.state.earningsCloseAccountId || earningsClose.designatedAccountId || earningsClose.suggestedAccountId || null;
		await this.update({ preview, choices, earningsCloseAccountId, error: "" });
	};

	onChooseEarningsCloseAccount = earningsCloseAccountId => this.update({ earningsCloseAccountId });

	refreshChartAccounts = async () => {
		const chart = await BookkeepingService.getClientCoA({ clientId: this.props.clientId });
		const chartAccounts = chart && chart.source === "client" ? chart.accounts : [];
		await this.update({ chartAccounts });
	};

	onPull = async () => {
		const { clientId, wpfEngagementId } = this.props;
		await this.update({ pulling: true, error: "", committed: null, acknowledged: {} });
		const preview = await BookkeepingService.pullQuickBooksTrialBalance({ clientId, wpfEngagementId });
		if (preview.error) {
			await this.update({ pulling: false, error: preview.error });
			return;
		}
		await this.update({ pulling: false });
		await this.showPreview({ preview });
	};

	onChooseChartAccount = ({ qboAccountId, chartAccountId }) => {
		const current = this.state.choices[qboAccountId] || {};
		const createsANewChartAccount = chartAccountId === NEW_CHART_ACCOUNT;
		const choice = { ...current, chartAccountId: createsANewChartAccount ? null : chartAccountId, createsANewChartAccount };
		const choices = { ...this.state.choices, [qboAccountId]: choice };
		this.update({ choices });
	};

	onChooseStatementLine = ({ qboAccountId, canonicalCode }) => {
		const current = this.state.choices[qboAccountId] || {};
		const choices = { ...this.state.choices, [qboAccountId]: { ...current, canonicalCode } };
		this.update({ choices });
	};

	onLinkAccounts = async () => {
		const { clientId } = this.props;
		const { preview, choices } = this.state;
		const links = BookkeepingService.describeQuickBooksLinks({ accountsNeedingLinking: preview.accountsNeedingLinking, choices });

		await this.update({ linking: true, error: "" });
		const answer = await BookkeepingService.linkQuickBooksAccounts({ clientId, documentId: preview.documentId, links });
		if (answer.error) {
			await this.update({ linking: false, error: answer.error });
			return;
		}
		await this.update({ linking: false });
		await this.refreshChartAccounts();
		await this.showPreview({ preview: answer.preview });
	};

	onUnlink = async ({ chartAccountId }) => {
		const { clientId } = this.props;
		const { preview } = this.state;
		await this.update({ linking: true, error: "" });
		const answer = await BookkeepingService.unlinkQuickBooksAccount({ clientId, chartAccountId, documentId: preview.documentId });
		if (answer.error) {
			await this.update({ linking: false, error: answer.error });
			return;
		}
		await this.update({ linking: false });
		await this.refreshChartAccounts();
		await this.showPreview({ preview: answer.preview });
	};

	onAcknowledge = ({ code, checked }) => {
		const acknowledged = { ...this.state.acknowledged, [code]: checked };
		this.update({ acknowledged });
	};

	onCommit = async () => {
		const { clientId, wpfEngagementId, onImported } = this.props;
		const { preview, acknowledged, earningsCloseAccountId } = this.state;
		const acknowledgedWarnings = Object.keys(acknowledged).filter(code => acknowledged[code]);

		await this.update({ committing: true, error: "" });
		const result = await BookkeepingService.commitQuickBooksTrialBalance({ clientId, wpfEngagementId, documentId: preview.documentId, acknowledgedWarnings, earningsCloseAccountId });
		if (result.error) {
			await this.update({ committing: false, error: result.error });
			return;
		}
		await this.update({ committing: false, committed: result });
		if (onImported) onImported({ importedTrialBalance: result.importedTrialBalance });
	};

	onClearImport = async () => {
		const { wpfEngagementId, onCleared } = this.props;
		await this.update({ clearing: true, error: "" });
		const cleared = await BookkeepingService.clearTrialBalanceImport({ wpfEngagementId });
		if (!cleared || cleared.error) {
			await this.update({ clearing: false, error: `The imported trial balance could not be cleared: ${(cleared && cleared.error) || "no response"}` });
			return;
		}
		await this.update({ clearing: false, committed: null });
		if (onCleared) onCleared();
	};

	onToggleAllLines = () => this.update({ showAllLines: !this.state.showAllLines });

	listChartAccountOptions = () => {
		const options = this.state.chartAccounts.map(account => {
			const isLinkedElsewhere = !!account.qbo_account_id;
			const name = BookkeepingService.describeChartAccount({ account });
			const label = isLinkedElsewhere ? `${name} (linked to QuickBooks)` : name;
			return { value: account.id, label };
		});
		const newAccountOption = { value: NEW_CHART_ACCOUNT, label: "New chart account from QuickBooks" };
		return [newAccountOption, ...options];
	};

	renderCurrentImport = () => {
		const { currentImport } = this.props;
		if (!currentImport || this.state.committed) return null;
		return (
			<div className="tk-notice qbtb__current" data-test="qbo-current-import">
				<p className="tk-notice__detail">
					This engagement's books are {BookkeepingService.describeTheBooksSource({ importedTrialBalance: currentImport })}. Saving a pull from
					QuickBooks replaces them.
				</p>
				<button type="button" className="tk-text-action tk-text-action--muted" disabled={this.state.clearing} onClick={this.onClearImport}>
					{this.state.clearing ? "Clearing…" : "Clear the imported trial balance"}
				</button>
			</div>
		);
	};

	renderNotConnected = () => {
		return (
			<div className="tk-notice tk-notice--warning" data-test="qbo-not-connected">
				<div className="tk-notice__head">
					<span className="tk-notice__title">
						<Icon.Link2 size={14} />
						This client isn't connected to QuickBooks Online
					</span>
				</div>
				<p className="tk-notice__detail">
					Connect the client's QuickBooks company from the client's Connections page, then come back here to pull its trial balance.
				</p>
			</div>
		);
	};

	renderStart = () => {
		return (
			<div className="qbtb__start" data-test="qbo-start">
				<p className="tk-lead">
					For a client who keeps their own books in QuickBooks Online. We'll read this year's and last year's trial balance straight from
					QuickBooks, check them against QuickBooks's own profit and loss and balance sheet, and use them for the financial statements.
				</p>
				<p className="tk-lead tk-lead--muted">Nothing is saved until you choose to. QuickBooks's answer is kept on the file so the figures can always be traced back to it.</p>
				<button type="button" className="tk-button tk-button--primary" data-test="qbo-pull" disabled={this.state.pulling} onClick={this.onPull}>
					{this.state.pulling ? "Reading QuickBooks…" : "Pull from QuickBooks"}
				</button>
			</div>
		);
	};

	renderPullSummary = () => {
		const { preview, pulling } = this.state;
		const { pull } = preview;
		const pulledAt = UtilityService.formatDateTime({ value: pull.pulledAt });
		const periodText = ({ period }) => `${UtilityService.formatDate({ value: period.periodStart })} to ${UtilityService.formatDate({ value: period.periodEnd })}`;
		const meta = [
			{ icon: <Icon.Calendar size={13} />, text: `${periodText({ period: pull.currentPeriod })}, with ${periodText({ period: pull.priorPeriod })}` },
			{ icon: <Icon.Clock size={13} />, text: `Pulled ${pulledAt}` },
			{ icon: null, text: `${pull.basis || "Unknown"} basis · ${pull.currency || "currency not stated"}` },
		];
		return (
			<div className="qbtb__summary">
				<ToolsSummaryCard
					icon={<QuickBooksGlyph className="qbtb__glyph" />}
					tone="neutral"
					title={pull.companyName || `QuickBooks company ${pull.realmId}`}
					meta={meta}
				/>
				<button type="button" className="tk-text-action qbtb__pull-again" data-test="qbo-pull-again" disabled={pulling} onClick={this.onPull}>
					<Icon.RefreshCw size={13} /> {pulling ? "Reading QuickBooks…" : "Pull again"}
				</button>
			</div>
		);
	};

	renderFigure = ({ label, value, comparedLabel, comparedValue, agrees }) => {
		const comparedText = comparedValue === null ? "not reported" : UtilityService.formatCurrency(comparedValue);
		return (
			<div className={clsx("qbtb__figure", !agrees && "qbtb__figure--out")}>
				<span className="qbtb__figure-label">
					{label}
					{agrees ? <Icon.Check size={13} className="qbtb__figure-mark" /> : <Icon.X size={13} className="qbtb__figure-mark" />}
				</span>
				<span className="qbtb__figure-value">{UtilityService.formatCurrency(value)}</span>
				<span className="qbtb__figure-source">{comparedLabel} {comparedText}</span>
			</div>
		);
	};

	renderVerdict = ({ period, heading }) => {
		const { totals, crossCheck } = period;
		const agrees = totals.balances && crossCheck.agrees;
		return (
			<div className={clsx("qbtb__verdict", agrees ? "qbtb__verdict--agrees" : "qbtb__verdict--out")} data-test="qbo-verdict">
				<div className="qbtb__verdict-head">
					{agrees ? <Icon.CheckCircle size={16} /> : <Icon.AlertTriangle size={16} />}
					<span>{agrees ? `${heading} agrees with QuickBooks` : `${heading} does not agree with QuickBooks`}</span>
				</div>
				<div className="qbtb__figures">
					{this.renderFigure({ label: "Debits", value: totals.debitTotal, comparedLabel: "Credits", comparedValue: totals.creditTotal, agrees: totals.balances })}
					{this.renderFigure({ label: "Net income", value: crossCheck.netIncome.trialBalance, comparedLabel: "QuickBooks P&L", comparedValue: crossCheck.netIncome.quickBooks, agrees: crossCheck.netIncome.agrees })}
					{this.renderFigure({ label: "Total assets", value: crossCheck.totalAssets.trialBalance, comparedLabel: "QuickBooks balance sheet", comparedValue: crossCheck.totalAssets.quickBooks, agrees: crossCheck.totalAssets.agrees })}
				</div>
			</div>
		);
	};

	renderRefusals = () => {
		const { refusals } = this.state.preview;
		if (refusals.length === 0) return null;
		return (
			<div className="tk-notice tk-notice--danger" data-test="qbo-refusals">
				<div className="tk-notice__head">
					<span className="tk-notice__title">
						<Icon.AlertCircle size={14} />
						QuickBooks's figures can't be used as they are
					</span>
				</div>
				<ul className="qbtb__reasons">
					{refusals.map(reason => <li key={reason}>{reason}</li>)}
				</ul>
			</div>
		);
	};

	renderLinkRow = account => {
		const choice = this.state.choices[account.qboAccountId] || {};
		const isNewAccount = choice.createsANewChartAccount === true;
		const chosenValue = isNewAccount ? NEW_CHART_ACCOUNT : choice.chartAccountId || null;
		const chartAccountNeedsALine = !isNewAccount && account.linkedChartAccountWithoutAMapping === choice.chartAccountId;
		const proposalNote = BookkeepingService.describeQuickBooksLinkSuggestion({ account });
		const quickBooksDetail = [account.accountNumber, account.accountType, account.isInactive ? "inactive" : null].filter(Boolean).join(" · ");

		return (
			<div key={account.qboAccountId} className="qbtb__link-row" data-test="qbo-link-row">
				<div className="qbtb__link-account">
					<span className="qbtb__link-name">{account.accountName}</span>
					<span className="qbtb__link-detail">{quickBooksDetail}</span>
				</div>
				<div className="qbtb__link-choice">
					<ToolsSelect
						value={chosenValue}
						options={this.listChartAccountOptions()}
						onChange={chartAccountId => this.onChooseChartAccount({ qboAccountId: account.qboAccountId, chartAccountId })}
						placeholder="Choose a chart account"
						searchable
						inline
					/>
					{(isNewAccount || chartAccountNeedsALine) && (
						<ToolsSelect
							value={choice.canonicalCode || null}
							options={this.state.statementLines}
							onChange={canonicalCode => this.onChooseStatementLine({ qboAccountId: account.qboAccountId, canonicalCode })}
							placeholder="Choose its statement line"
							searchable
							inline
						/>
					)}
					{proposalNote && <span className="qbtb__link-note">{proposalNote}</span>}
				</div>
			</div>
		);
	};

	renderLinking = () => {
		const { preview, choices, linking } = this.state;
		const accounts = preview.accountsNeedingLinking;
		if (accounts.length === 0) return null;

		const links = BookkeepingService.describeQuickBooksLinks({ accountsNeedingLinking: accounts, choices });
		const everyAccountHasAChoice = links.length === accounts.length;

		return (
			<section className="qbtb__section" data-test="qbo-linking" ref={this.linkingSection}>
				<div className="tk-section-head">
					<h3 className="tk-section-title">Step 1 · Link QuickBooks accounts to the chart</h3>
					<span className="tk-section-count">{accounts.length}</span>
				</div>
				<p className="tk-lead tk-lead--muted">
					Once, per account. Each row already has a suggestion — check it, change any you disagree with, then link them all. QuickBooks
					keeps one id for each account whatever it's renamed to, and that id is what next year's pull is matched on.
				</p>
				<div className="qbtb__link-list">{accounts.map(this.renderLinkRow)}</div>
				<button
					type="button"
					className="tk-button tk-button--primary"
					data-test="qbo-link"
					disabled={linking || !everyAccountHasAChoice}
					title={everyAccountHasAChoice ? undefined : "Choose a chart account, or a statement line for a new one, for every account"}
					onClick={this.onLinkAccounts}
				>
					{linking ? "Linking…" : `Link ${UtilityService.describeCount({ count: accounts.length, singular: "account" })}`}
				</button>
			</section>
		);
	};

	renderWarnings = () => {
		const { preview, acknowledged } = this.state;
		if (preview.warnings.length === 0) return null;
		return (
			<section className="qbtb__section" data-test="qbo-warnings">
				<h3 className="tk-section-title">Before saving, confirm you've read these</h3>
				{preview.warnings.map(warning => (
					<ToolsAcknowledgeNotice
						key={warning.code}
						message={warning.message}
						checked={!!acknowledged[warning.code]}
						onChange={event => this.onAcknowledge({ code: warning.code, checked: event.target.checked })}
						label="I've read this and want to save anyway"
						className="qbtb__acknowledge"
						dataTest="qbo-warning"
					/>
				))}
			</section>
		);
	};

	renderEarningsClose = () => {
		const { preview, earningsCloseAccountId } = this.state;
		const earningsClose = preview.earningsClose;
		if (!earningsClose.required) return null;

		const options = earningsClose.equityAccounts.map(account => ({ value: account.id, label: BookkeepingService.describeChartAccount({ account }) }));
		const isSuggestion = earningsCloseAccountId && earningsCloseAccountId === earningsClose.suggestedAccountId && !earningsClose.designatedAccountId;
		const equityAccountsAreNotOnTheChartYet = options.length === 0;
		return (
			<section className="qbtb__section" data-test="qbo-earnings-close">
				<h3 className="tk-section-title">Step 2 · Where last year's profit closes</h3>
				<p className="tk-lead tk-lead--muted">
					QuickBooks's prior year still holds {UtilityService.formatCurrency(earningsClose.netIncome)} of profit in its revenue and expense
					accounts. It closes into one equity account to give this year's opening balances and the retained earnings statement's opening.
				</p>
				{equityAccountsAreNotOnTheChartYet ? (
					<p className="qbtb__waiting" data-test="qbo-earnings-close-waiting">
						<Icon.Clock size={14} />
						<span>QuickBooks's equity accounts, such as Retained Earnings, join the chart when they're linked in step 1. Link them first, then choose one here.</span>
						<button type="button" className="tk-text-action" onClick={this.onGoToLinking}>Go to step 1</button>
					</p>
				) : (
					<div className="qbtb__close-choice">
						<ToolsSelect
							value={earningsCloseAccountId}
							options={options}
							onChange={this.onChooseEarningsCloseAccount}
							placeholder="Choose an equity account"
						/>
						{isSuggestion && <span className="qbtb__link-note">Suggested for this kind of entity — recorded when you save.</span>}
					</div>
				)}
			</section>
		);
	};

	renderJournalSettlement = () => {
		const { preview } = this.state;
		const settlement = BookkeepingService.describeJournalSettlementPreview({ journalSettlement: preview.journalSettlement });
		if (!settlement) return null;
		return (
			<section className="qbtb__section" data-test="qbo-journal-settlement">
				<h3 className="tk-section-title">Entries this file booked into QuickBooks</h3>
				<p className="tk-lead tk-lead--muted">{settlement}</p>
			</section>
		);
	};

	renderSignedYearComparison = () => {
		const { preview } = this.state;
		if (!preview.comparativesComeFromASignedYear) return null;
		const comparison = preview.openingsAgainstSignedYear;
		return (
			<section className="qbtb__section" data-test="qbo-signed-year">
				<h3 className="tk-section-title">Last year was signed off in Docify</h3>
				<p className="tk-lead tk-lead--muted">
					The comparative column comes from the signed statements, not from QuickBooks. QuickBooks's closing balances for that year are
					compared with them so any year-end adjustment that never reached QuickBooks is visible.
				</p>
				{comparison.agrees ? (
					<p className="qbtb__agrees"><Icon.Check size={14} /> QuickBooks's prior year matches the signed closing balances.</p>
				) : (
					<div className="qbtb__table">
						{comparison.differences.map(difference => (
							<div key={difference.canonicalCode} className="tk-ledger__row qbtb__table-row">
								<span className="qbtb__table-name">{difference.canonicalName}</span>
								<span className="tk-ledger__amount">Signed {UtilityService.formatCurrency(difference.signed)}</span>
								<span className="tk-ledger__amount">QuickBooks {UtilityService.formatCurrency(difference.quickBooks)}</span>
								<span className="tk-ledger__amount qbtb__table-amount--out">{UtilityService.formatCurrency(difference.difference)}</span>
							</div>
						))}
						{comparison.unlinkedAccounts > 0 && (
							<p className="tk-lead tk-lead--muted">{UtilityService.describeCount({ count: comparison.unlinkedAccounts, singular: "prior-year account" })} not linked yet, so not compared.</p>
						)}
					</div>
				)}
			</section>
		);
	};

	renderLine = line => {
		const chart = line.chartAccount;
		const hasAStatementLine = !!(chart && chart.canonicalCode);
		const filedUnder = !chart ? "Not linked" : hasAStatementLine ? chart.canonicalName : "No statement line yet";
		return (
			<div key={line.qboAccountId} className="tk-ledger__row qbtb__table-row">
				<span className="qbtb__table-name">
					{line.accountName}
					{line.isInactive && <span className="tk-badge tk-badge--warning qbtb__inactive">Inactive</span>}
				</span>
				<span className={clsx("qbtb__table-filed", !chart && "qbtb__table-filed--missing")}>{filedUnder}</span>
				<span className="tk-ledger__amount">{line.debit ? UtilityService.formatCurrency(line.debit) : ""}</span>
				<span className="tk-ledger__amount">{line.credit ? UtilityService.formatCurrency(line.credit) : ""}</span>
				{chart && (
					<button type="button" className="tk-text-action tk-text-action--xs tk-text-action--muted" disabled={this.state.linking} onClick={() => this.onUnlink({ chartAccountId: chart.id })}>
						Unlink
					</button>
				)}
			</div>
		);
	};

	renderLines = () => {
		const { preview, showAllLines } = this.state;
		const lines = preview.current.lines;
		const shownLines = showAllLines ? lines : lines.slice(0, LINES_SHOWN_BEFORE_EXPANDING);
		const hiddenCount = lines.length - shownLines.length;
		return (
			<section className="qbtb__section" data-test="qbo-lines">
				<div className="tk-section-head">
					<h3 className="tk-section-title">This year's trial balance</h3>
					<span className="tk-section-count">{lines.length}</span>
				</div>
				<div className="qbtb__table">
					<div className="tk-ledger__row tk-ledger__row--head qbtb__table-row">
						<span>QuickBooks account</span>
						<span>Statement line</span>
						<span className="tk-ledger__amount">Debit</span>
						<span className="tk-ledger__amount">Credit</span>
						<span />
					</div>
					{shownLines.map(this.renderLine)}
				</div>
				{(hiddenCount > 0 || showAllLines) && (
					<button type="button" className="tk-text-action tk-text-action--xs" onClick={this.onToggleAllLines}>
						{showAllLines ? "Show fewer" : `Show ${hiddenCount} more`}
					</button>
				)}
			</section>
		);
	};

	renderCommit = () => {
		const { preview, acknowledged, committing, committed } = this.state;
		if (committed) {
			const priorNote = committed.priorYearFiguresSaved > 0 ? ` and ${committed.priorYearFiguresSaved} prior-year figures` : "";
			const journalsNote = BookkeepingService.describeJournalsSettledByAPull({ journalsSettled: committed.journalsSettled });
			return (
				<div className="tk-notice tk-notice--success" data-test="qbo-committed">
					<div className="tk-notice__head">
						<span className="tk-notice__title">
							<Icon.CheckCircle size={14} />
							These are now this file's books
						</span>
					</div>
					<p className="tk-notice__detail">
						{committed.imported} balances{priorNote} saved from QuickBooks. Next: generate the financial statements. To undo, choose
						Change the books on the file and clear the imported trial balance.
					</p>
					{journalsNote && <p className="tk-notice__detail" data-test="qbo-journals-settled">{journalsNote}</p>}
				</div>
			);
		}

		const unacknowledged = preview.warnings.filter(warning => !acknowledged[warning.code]);
		const closeAccountIsMissing = preview.earningsClose.required && !this.state.earningsCloseAccountId;
		const canSave = preview.canCommit && unacknowledged.length === 0 && !closeAccountIsMissing;
		const nextStep = BookkeepingService.describeNextQuickBooksStep({ preview, closeAccountIsMissing, unacknowledgedCount: unacknowledged.length });
		const accountsStillNeedLinking = preview.accountsNeedingLinking.length > 0;

		return (
			<div className="qbtb__commit">
				<button type="button" className={clsx("tk-button", accountsStillNeedLinking ? "tk-button--secondary" : "tk-button--primary")} data-test="qbo-commit" disabled={committing || !canSave} onClick={this.onCommit}>
					{committing ? "Saving…" : "Save as this file's books"}
				</button>
				{nextStep ? (
					<span className="qbtb__next-step" data-test="qbo-next-step">
						{nextStep}
						{accountsStillNeedLinking && <button type="button" className="tk-text-action" onClick={this.onGoToLinking}>Go to step 1</button>}
					</span>
				) : (
					<span className="tk-form-help">Replaces anything imported before, and can be undone. Saving changes nothing in QuickBooks.</span>
				)}
			</div>
		);
	};

	renderPreview = () => {
		const { preview } = this.state;
		return (
			<React.Fragment>
				{this.renderPullSummary()}
				{this.renderVerdict({ period: preview.current, heading: "This year" })}
				{!preview.comparativesComeFromASignedYear && preview.prior.lines.length > 0 && this.renderVerdict({ period: preview.prior, heading: "Last year" })}
				{this.renderRefusals()}
				{this.renderLinking()}
				{this.renderEarningsClose()}
				{this.renderWarnings()}
				{this.renderJournalSettlement()}
				{this.renderSignedYearComparison()}
				{this.renderLines()}
				{this.renderCommit()}
			</React.Fragment>
		);
	};

	render() {
		const { loading, connection, preview, error } = this.state;
		if (loading) return <p className="tk-lead">Checking the client's QuickBooks connection…</p>;

		const isConnected = !!(connection && connection.connected);
		const isKnownToBeDisconnected = !!(connection && connection.connected === false && !connection.error);
		return (
			<div className="qbtb" data-test="quickbooks-trial-balance">
				{this.renderCurrentImport()}
				{error && <div className="tk-notice tk-notice--danger" data-test="qbo-error"><p className="tk-notice__detail">{error}</p></div>}
				{isKnownToBeDisconnected && !preview && this.renderNotConnected()}
				{isConnected && !preview && this.renderStart()}
				{preview && this.renderPreview()}
			</div>
		);
	}
}

export default QuickBooksTrialBalancePanel;
